import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ReferralEarningsChart.css';
import SoundManager from '../services/SoundManager';

// Хелпер для виброотклика
const triggerHaptic = (type: 'light' | 'medium' | 'heavy' = 'light') => {
  if ((window as any).Telegram?.WebApp?.HapticFeedback) {
    (window as any).Telegram.WebApp.HapticFeedback.impactOccurred(type);
  }
};

interface DailyEarnings {
  date: string;
  daily_sperm: number;
  deposit_sperm: number;
  daily_usdt: number;
  deposit_usdt: number;
  total: number;
}

interface ReferralEarningsChartProps {
  userId?: number;
}

const ReferralEarningsChart: React.FC<ReferralEarningsChartProps> = ({ userId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<DailyEarnings[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'7d' | '30d'>('7d');
  const [level, setLevel] = useState<'all' | '1' | '2' | '3' | '4' | '5'>('all');
  const [totals, setTotals] = useState({
    daily_sperm: 0,
    deposit_sperm: 0,
    daily_usdt: 0,
    deposit_usdt: 0,
    total: 0
  });
  const [selectedBar, setSelectedBar] = useState<number | null>(null);

  useEffect(() => {
    fetchEarningsData();
  }, [period, level]);

  const fetchEarningsData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/referrals/earnings-chart?period=${period}&level=${level}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData(result.data.dailyEarnings || []);
          setTotals(result.data.totals || {
            daily_sperm: 0,
            deposit_sperm: 0,
            daily_usdt: 0,
            deposit_usdt: 0,
            total: 0
          });
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки данных графика:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMaxValue = () => {
    if (data.length === 0) return 100;
    return Math.max(...data.map(d => d.total), 10);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getDate()}/${date.getMonth() + 1}`;
  };

  const maxValue = getMaxValue();

  if (loading) {
    return (
      <div className="referral-earnings-chart loading">
        <div className="chart-loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="referral-earnings-chart empty">
        <div className="chart-empty">
          📊 {t('referralChart.noData')}
        </div>
      </div>
    );
  }

  return (
    <div className="referral-earnings-chart">
      <div className="chart-header">
        <h3>📈 {t('referralChart.title')}</h3>
        <div className="chart-controls">
          <div className="chart-period-selector">
            <button
              className={`period-btn ${period === '7d' ? 'active' : ''}`}
              onClick={() => { SoundManager.playButtonClick(); triggerHaptic(); setPeriod('7d'); }}
            >
              {t('referralChart.days7')}
            </button>
            <button
              className={`period-btn ${period === '30d' ? 'active' : ''}`}
              onClick={() => { SoundManager.playButtonClick(); triggerHaptic(); setPeriod('30d'); }}
            >
              {t('referralChart.days30')}
            </button>
          </div>
          <div className="chart-level-selector">
            <button
              className={`level-btn ${level === 'all' ? 'active' : ''}`}
              onClick={() => { SoundManager.playButtonClick(); triggerHaptic(); setLevel('all'); }}
            >
              {t('referralChart.all')}
            </button>
            <button
              className={`level-btn ${level === '1' ? 'active' : ''}`}
              onClick={() => { SoundManager.playButtonClick(); triggerHaptic(); setLevel('1'); }}
            >
              1
            </button>
            <button
              className={`level-btn ${level === '2' ? 'active' : ''}`}
              onClick={() => { SoundManager.playButtonClick(); triggerHaptic(); setLevel('2'); }}
            >
              2
            </button>
            <button
              className={`level-btn ${level === '3' ? 'active' : ''}`}
              onClick={() => { SoundManager.playButtonClick(); triggerHaptic(); setLevel('3'); }}
            >
              3
            </button>
            <button
              className={`level-btn ${level === '4' ? 'active' : ''}`}
              onClick={() => { SoundManager.playButtonClick(); triggerHaptic(); setLevel('4'); }}
            >
              4
            </button>
            <button
              className={`level-btn ${level === '5' ? 'active' : ''}`}
              onClick={() => { SoundManager.playButtonClick(); triggerHaptic(); setLevel('5'); }}
            >
              5
            </button>
          </div>
        </div>
      </div>

      <div className="chart-summary">
        <div className="summary-item">
          <span className="summary-label">💎 {t('referralChart.dailySperm')}:</span>
          <span className="summary-value daily-sperm">{(totals.daily_sperm || 0).toFixed(2)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">💰 {t('referralChart.onetimeSperm')}:</span>
          <span className="summary-value deposit-sperm">{(totals.deposit_sperm || 0).toFixed(2)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">💵 {t('referralChart.usdtClaim')}:</span>
          <span className="summary-value daily-usdt">{(totals.daily_usdt || 0).toFixed(2)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">💸 {t('referralChart.onetimeUsdt')}:</span>
          <span className="summary-value deposit-usdt">{(totals.deposit_usdt || 0).toFixed(2)}</span>
        </div>
        <div className="summary-item total">
          <span className="summary-label">✨ {t('referralChart.total')}:</span>
          <span className="summary-value">{(totals.daily_sperm || 0).toFixed(2)} + {(totals.deposit_sperm || 0).toFixed(2)} SPERM | {(totals.daily_usdt || 0).toFixed(2)} + {(totals.deposit_usdt || 0).toFixed(2)} USDT</span>
        </div>
      </div>

      <div className="chart-container">
        <div className="chart-bars">
          {data.map((item, index) => {
            const dailySpermHeight = ((item.daily_sperm || 0) / maxValue) * 100;
            const depositSpermHeight = ((item.deposit_sperm || 0) / maxValue) * 100;
            const dailyUsdtHeight = ((item.daily_usdt || 0) / maxValue) * 100;
            const depositUsdtHeight = ((item.deposit_usdt || 0) / maxValue) * 100;

            return (
              <div
                key={index}
                className={`chart-bar-wrapper ${selectedBar === index ? 'selected' : ''}`}
                onClick={() => { SoundManager.playButtonClick(); triggerHaptic(); setSelectedBar(selectedBar === index ? null : index); }}
              >
                <div className="chart-bar" style={{ height: '120px' }}>
                  {item.total > 0 && (
                    <>
                      <div
                        className="bar-segment bar-daily-sperm"
                        style={{ height: `${dailySpermHeight}%` }}
                        title={`${t('referralChart.dailySperm')}: ${(item.daily_sperm || 0).toFixed(2)}`}
                      />
                      <div
                        className="bar-segment bar-deposit-sperm"
                        style={{ height: `${depositSpermHeight}%` }}
                        title={`${t('referralChart.onetimeSperm')}: ${(item.deposit_sperm || 0).toFixed(2)}`}
                      />
                      <div
                        className="bar-segment bar-daily-usdt"
                        style={{ height: `${dailyUsdtHeight}%` }}
                        title={`${t('referralChart.dailyUsdt')}: ${(item.daily_usdt || 0).toFixed(2)}`}
                      />
                      <div
                        className="bar-segment bar-deposit-usdt"
                        style={{ height: `${depositUsdtHeight}%` }}
                        title={`${t('referralChart.onetimeUsdt')}: ${(item.deposit_usdt || 0).toFixed(2)}`}
                      />
                    </>
                  )}
                  {item.total === 0 && (
                    <div className="bar-empty" />
                  )}
                </div>
                <div className="chart-bar-label">{formatDate(item.date)}</div>
                <div className="chart-bar-values">
                  {(item.daily_sperm || 0) > 0 && (
                    <div className="chart-bar-value daily-sperm">💎 {(item.daily_sperm || 0).toFixed(1)}</div>
                  )}
                  {(item.deposit_sperm || 0) > 0 && (
                    <div className="chart-bar-value deposit-sperm">💰 {(item.deposit_sperm || 0).toFixed(1)}</div>
                  )}
                  {(item.daily_usdt || 0) > 0 && (
                    <div className="chart-bar-value daily-usdt">💵 {(item.daily_usdt || 0).toFixed(1)}</div>
                  )}
                  {(item.deposit_usdt || 0) > 0 && (
                    <div className="chart-bar-value deposit-usdt">💸 {(item.deposit_usdt || 0).toFixed(1)}</div>
                  )}
                </div>

                {/* Тултип при клике */}
                {selectedBar === index && item.total > 0 && (
                  <div className="chart-tooltip">
                    <div className="tooltip-date">{formatDate(item.date)}</div>
                    {(item.daily_sperm || 0) > 0 && (
                      <div className="tooltip-row">
                        <span className="tooltip-label daily-sperm">💎 {t('referralChart.daily')}:</span>
                        <span className="tooltip-value">{(item.daily_sperm || 0).toFixed(2)} SPERM</span>
                      </div>
                    )}
                    {(item.deposit_sperm || 0) > 0 && (
                      <div className="tooltip-row">
                        <span className="tooltip-label deposit-sperm">💰 {t('referralChart.onetime')}:</span>
                        <span className="tooltip-value">{(item.deposit_sperm || 0).toFixed(2)} SPERM</span>
                      </div>
                    )}
                    {(item.daily_usdt || 0) > 0 && (
                      <div className="tooltip-row">
                        <span className="tooltip-label daily-usdt">💵 {t('referralChart.atClaim')}:</span>
                        <span className="tooltip-value">{(item.daily_usdt || 0).toFixed(2)} USDT</span>
                      </div>
                    )}
                    {(item.deposit_usdt || 0) > 0 && (
                      <div className="tooltip-row">
                        <span className="tooltip-label deposit-usdt">💸 {t('referralChart.onetime')}:</span>
                        <span className="tooltip-value">{(item.deposit_usdt || 0).toFixed(2)} USDT</span>
                      </div>
                    )}
                    <div className="tooltip-row total">
                      <span className="tooltip-label">✨ {t('referralChart.total')}:</span>
                      <span className="tooltip-value">
                        {((item.daily_sperm || 0) + (item.deposit_sperm || 0)).toFixed(2)} SPERM<br/>
                        {((item.daily_usdt || 0) + (item.deposit_usdt || 0)).toFixed(2)} USDT
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="chart-legend">
        <div className="legend-item">
          <span className="legend-color daily-sperm"></span>
          <span className="legend-text">💎 {t('referralChart.dailySperm')}</span>
        </div>
        <div className="legend-item">
          <span className="legend-color deposit-sperm"></span>
          <span className="legend-text">💰 {t('referralChart.onetimeSperm')}</span>
        </div>
        <div className="legend-item">
          <span className="legend-color daily-usdt"></span>
          <span className="legend-text">💵 {t('referralChart.usdtClaim')}</span>
        </div>
        <div className="legend-item">
          <span className="legend-color deposit-usdt"></span>
          <span className="legend-text">💸 {t('referralChart.onetimeUsdt')}</span>
        </div>
      </div>
    </div>
  );
};

export default ReferralEarningsChart;
